import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DOUBLE_CLICK_DELAY } from "../constants";
import {
  getCompoundForeignSpeakingOnlyTitle,
  status,
} from "shared/src/appointment.mjs";
import {
  faCircleExclamation,
  faCommentDots,
  faFaceAngry,
  faGlobe,
  faUserDoctor,
} from "@fortawesome/free-solid-svg-icons";
import * as d from "shared/src/date.mjs";
import { msToM } from "./resource-list/fn.mjs";
import {
  faAlarmClock,
  faHourglassClock,
} from "@fortawesome/pro-solid-svg-icons";
import * as f from "../utils/formatter";
import { getClientProblem } from "./Client.jsx";

/**
 * @typedef Appointment
 * @property {string} start
 * @property {string} end
 * @property {number} status_id
 * @property {string} calendar_id
 *
 * @typedef Event
 * @property {Date} start
 * @property {Date} end
 * @property {string} backgroundColor
 * @property {Appointment} extendedProps
 */

export const bufferService = { name: "Buffer", color: "#1584af" };

/**
 * @param {{status_id: number, service: {color: string}}} appointment
 *
 * @returns {string}
 */
export function appointmentColor(appointment) {
  return appointment.service?.color;
}

export function waitingAppointmentBackground(appointment) {
  return `color-mix(in srgb, ${appointmentColor(appointment)} 15%, #ffffff)`;
}

/**
 * @param {{status_id: number, service: {color: string}}} appointment
 *
 * @returns {string}
 */
export function appointmentBackground(appointment) {
  if (status.BOOKED === appointment.status_id) return "#ffffff";
  else if (status.CONFIRMED === appointment.status_id) return "#E8EFEF";

  return waitingAppointmentBackground(appointment);
}

/**
 * @param {{status_id: number, service: {color: string}}} appointment
 *
 * @returns {string}
 */
export function appointmentInfoColor(appointment) {
  if (status.BOOKED === appointment.status_id)
    return "var(--mui-palette-action-disabled)";

  return `color-mix(in srgb, ${appointmentColor(appointment)} 80%, #000000)`;
}

export function createClickHandler({ onClick, onDoubleClick }) {
  let timeoutId = null;
  const clear = () => {
    clearTimeout(timeoutId);
    timeoutId = null;
  };

  return function (...args) {
    if (timeoutId == null) {
      timeoutId = setTimeout(() => {
        clear();
        onClick(...args);
      }, DOUBLE_CLICK_DELAY);
      return;
    }

    clear();
    onDoubleClick(...args);
  };
}

export function getClientDoctors(client) {
  if (client?.any_doctor) {
    return ["Libovolný lékař"];
  }

  return client?.doctors?.map((d) => f.fullName(d)) ?? [];
}

function getAppointmentDoctors(appointment) {
  if (appointment?.any_doctor) {
    return ["Libovolný lékař"];
  }

  return appointment?.doctors?.map((d) => f.fullName(d)) ?? [];
}

export function getProblem(appointment) {
  return getClientProblem(appointment.client);
}

export function getCompoundDoctor(appointments) {
  const appointmentDoctors = appointments.flatMap((appointment) =>
    getAppointmentDoctors(appointment),
  );

  if (appointmentDoctors.length > 0) {
    return appointmentDoctors.join(", ");
  }

  const clientDoctors = appointments.flatMap((appointment) =>
    getClientDoctors(appointment?.client),
  );
  if (clientDoctors.length > 0) {
    return clientDoctors.join(", ");
  }

  const serviceRequiresDoctor = appointments.some(
    (appointment) => appointment?.service?.locationService?.requires_doctor,
  );

  if (serviceRequiresDoctor) {
    return "Libovolný lékař";
  }
}

function getCompoundProblem(appointments) {
  const problems = appointments
    .map((a) => getProblem(a))
    .filter((p) => p != null);

  if (problems.length === 0) {
    return null;
  }

  return problems.join("\n");
}

export function getProblematicTreatmentLinks(client) {
  const links =
    client?.problematicTreatments
      ?.flatMap((t) => t.plans)
      ?.map((p) => p.link) ?? [];

  return links;
}

function getCompoundProblematicTreatmentLinks(appointments) {
  return appointments.flatMap((appointment) =>
    getProblematicTreatmentLinks(appointment.client),
  );
}

export function TagList({ appointments, disable }) {
  if (disable) {
    return <div className="tag-list"></div>;
  }

  const problem = getCompoundProblem(appointments);

  const allNotes = appointments.flatMap(
    (a) => a?.client?.nextAppointmentNotes ?? [],
  );

  const sortedNotes = [...allNotes].sort((a, b) => {
    const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });

  const note =
    sortedNotes
      .map((n) => (n?.note ?? "").trim())
      .filter(Boolean)
      .join("\n—\n") || null;

  const languages = getCompoundForeignSpeakingOnlyTitle(appointments);
  const doctor = getCompoundDoctor(appointments);
  const problematicTreatmentLinks =
    getCompoundProblematicTreatmentLinks(appointments);

  return (
    <div className="tag-list">
      {problem != null && (
        <span className="tag-wrapper" title={problem}>
          <FontAwesomeIcon className="tag tag-face" icon={faFaceAngry} />
        </span>
      )}
      {problematicTreatmentLinks.length > 0 && (
        <span className="tag-wrapper" title="Problémová léčba">
          <FontAwesomeIcon
            className="tag tag-exclamation"
            icon={faCircleExclamation}
          />
        </span>
      )}
      {languages != null && (
        <span className="tag-wrapper" title={languages}>
          <FontAwesomeIcon className="tag tag-globe" icon={faGlobe} />
        </span>
      )}
      {note != null && (
        <span className="tag-wrapper" title={note}>
          <FontAwesomeIcon className="tag tag-bubble" icon={faCommentDots} />
        </span>
      )}
      {doctor != null && (
        <span className="tag-wrapper" title={doctor}>
          <FontAwesomeIcon className="tag tag-doctor" icon={faUserDoctor} />
        </span>
      )}
    </div>
  );
}

function getPresentSinceAudit(data) {
  let last = null;
  for (let i = data.length - 1; i >= 0; i--) {
    const current = data[i];
    const status_id = data[i].changed_fields?.status_id;
    if (status_id == null) {
      continue;
    }

    if (status_id === String(status.CONFIRMED)) {
      return current;
    }

    if (status_id === String(status.WAITING)) {
      last = current;
    }
  }

  if (last != null) {
    return last;
  }

  const first = data[0];
  const created_status_id = first?.row_data?.status_id;
  if (
    created_status_id === String(status.CONFIRMED) ||
    created_status_id === String(status.WAITING)
  ) {
    return first;
  }
}

export const waitingInfoStatuses = new Set([
  status.WAITING,
  status.CONFIRMED,
  status.ASSIGNED,
]);

function getCheckedIn(appointment) {
  if (!waitingInfoStatuses.has(appointment.status_id)) {
    return;
  }

  const data = appointment.audit.data;
  const audit = getPresentSinceAudit(data);
  if (audit != null) {
    return d.parseUtcDateTime(audit.created_at);
  }
}

export function rawWaitingTimes(appointment, now) {
  const checkedInTime = getCheckedIn(appointment);

  if (checkedInTime != null) {
    const appointmentTime = d.parseDateTime(appointment.start);
    const checkedInMinutes = msToM(checkedInTime - appointmentTime);

    return {
      checked_in_minutes: checkedInMinutes,
      waiting_minutes: msToM(
        checkedInMinutes < 0 ? now - appointmentTime : now - checkedInTime,
      ),
    };
  }
}

export function waitingTimesIcons(rawWaitingTimes) {
  if (rawWaitingTimes == null) {
    return null;
  }

  return {
    checked_in_minutes: (
      <span
        style={{
          color:
            rawWaitingTimes.checked_in_minutes > 0
              ? "var(--mui-palette-_components-alert-error-color)"
              : "var(--mui-palette-_components-alert-info-color)",
        }}
      >
        <FontAwesomeIcon icon={faHourglassClock} />{" "}
        {f.absoluteMinutesToDuration(rawWaitingTimes.checked_in_minutes)}
      </span>
    ),
    waiting_minutes: (
      <span
        style={{
          color:
            rawWaitingTimes.waiting_minutes > 0
              ? "var(--mui-palette-_components-alert-error-color)"
              : "var(--mui-palette-_components-alert-info-color)",
        }}
      >
        <FontAwesomeIcon icon={faAlarmClock} />{" "}
        {f.absoluteMinutesToDuration(rawWaitingTimes.waiting_minutes)}
      </span>
    ),
  };
}

export function adhocWaitingTimesIcons(rawWaitingTimes) {
  if (rawWaitingTimes == null) {
    return null;
  }

  return {
    waiting_minutes: (
      <span
        style={{
          color: "var(--mui-palette-_components-alert-info-color)",
          whiteSpace: "nowrap",
        }}
      >
        <FontAwesomeIcon icon={faAlarmClock} />{" "}
        {f.minutesToDuration(rawWaitingTimes.waiting_minutes)}
      </span>
    ),
  };
}
